import clone from 'clone';
import moment from 'moment';
import {
    AdvancedFilterConditionOperator,
    AdvancedFilterDto,
    AdvancedFilterLogicalOperator,
    BasicFilterDto,
    BasicFilterOperator,
    DynamicFilterDto,
    ExprFilterDto,
    FilterDto,
    FilterType,
    RelativeDateFilterDto,
    RelativeDateOrTimeOperator,
    RelativeDateTimeFilterUnit,
    RelativeTimeFilterDto,
    SchemaFieldTargetDto,
    TopNFilterDto,
    TopNFilterOperator,
    ValueDataType,
} from 'src/app/services/api.services';
import { isNullOrUndefined } from '../../shared/utils';
import { isNumericDataType } from './data-type';

export type Filter =
    FilterDto
  | AdvancedFilterDto
  | BasicFilterDto
  | RelativeDateFilterDto
  | RelativeTimeFilterDto
  | TopNFilterDto
  | DynamicFilterDto
  | ExprFilterDto;

  export function isAdvancedFilter(filter: FilterDto): filter is AdvancedFilterDto {
    return filter.filterType === FilterType.Advanced;
  }

  export function isBasicFilter(filter: FilterDto): filter is BasicFilterDto {
    return filter.filterType === FilterType.Basic;
  }

  export function isRelativeDateFilter(filter: FilterDto): filter is RelativeDateFilterDto {
    return filter.filterType === FilterType.RelativeDate;
  }

  export function isRelativeTimeFilter(filter: FilterDto): filter is RelativeTimeFilterDto {
    return filter.filterType === FilterType.RelativeTime;
  }

  export function isTopNFilter(filter: FilterDto): filter is TopNFilterDto {
    return filter.filterType === FilterType.TopN;
  }

  export function isDynamicFilter(filter: FilterDto): filter is DynamicFilterDto {
    return filter.filterType === FilterType.Dynamic;
  }
  
  export function isExprFilter(filter: FilterDto): filter is ExprFilterDto {
    return filter.filterType === FilterType.Expr;
  }


  export function hasFilterTarget(filter: FilterDto) {
    if (!filter)
      return false;

    const target = (<any>filter)['target'];

    return !isNullOrUndefined(target);
  }

  export function getFilterTarget(filter: FilterDto) {
    if (!filter)
      return null;

    return <SchemaFieldTargetDto>(<any>filter)['target'];
  }

  export function setFilterTarget(filter: FilterDto, target: SchemaFieldTargetDto) {
    (<any>filter)['target'] = target;
  }

  export function withFilterTarget(filter: FilterDto, target: SchemaFieldTargetDto, preserveAggregation = false) {
    filter = clone(filter);

    if (preserveAggregation) {
      const currentTarget = getFilterTarget(filter);

      if (currentTarget && !isNullOrUndefined(currentTarget.aggregation)) {
        target = {
          ...target, aggregation: currentTarget.aggregation
        };
      }
    }

    setFilterTarget(filter, target);

    return filter;
  }

  export function getReferencedTargets(filter: FilterDto): SchemaFieldTargetDto[] {
    if (!filter)
      return [];

    const list = [];

    const mainTarget = getFilterTarget(filter);
    if (mainTarget)
      list.push(mainTarget);

    if (isTopNFilter(filter) && filter.orderBy)
      list.push(filter.orderBy);

    return list;
  }

  export function getDefaultFilterConditionValue(dataType: ValueDataType) {
    // INFO: IN-3619
    // Previously we defaulted to specific constants, but we don't actually want that from requirements point of view
    // I am leaving this method, as if we choose to add defaults, we can do it here without updating more of the code
    if (dataType == ValueDataType.TimeOnly)
      return '00:00:00';

    return null;
  }

export const DataTypes = <DataTypeConfig[]>[
  {
    name: 'Unknown',
    id: ValueDataType.Unknown,
    support: {
      advanced: true,
      blank: true,
      compare: true,
      equality: true,
      text: false,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: false,
      topN: true,
      tuple: false,
      identity: false,
      dynamic: true
    }
  },
  {
    name: 'Text',
    id: ValueDataType.Text,
    support: {
      advanced: true,
      blank: true,
      compare: false,
      equality: true,
      text: true,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: false,
      topN: true,
      tuple: false,
      identity: false,
      dynamic: true
    }
  },
  {
    name: 'Decimal',
    id: ValueDataType.Decimal,
    support: {
      advanced: true,
      blank: true,
      compare: true,
      equality: true,
      text: false,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: false,
      topN: true,
      tuple: false,
      identity: false,
      dynamic: false
    }
  },
  {
    name: 'Number',
    id: ValueDataType.Number,
    support: {
      advanced: true,
      blank: true,
      compare: true,
      equality: true,
      text: false,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: false,
      topN: true,
      tuple: false,
      identity: false,
      dynamic: false
    }
  },
  {
    name: 'Integer',
    id: ValueDataType.Integer,
    support: {
      advanced: true,
      blank: true,
      compare: true,
      equality: true,
      text: false,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: false,
      topN: true,
      tuple: false,
      identity: false,
      dynamic: false
    }
  },
  {
    name: 'Boolean',
    id: ValueDataType.Boolean,
    support: {
      advanced: false,
      blank: true,
      compare: false,
      equality: true,
      text: false,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: false,
      topN: false,
      tuple: false,
      identity: false,
      dynamic: false
    }
  },
  {
    name: 'Date',
    id: ValueDataType.DateOnly,
    support: {
      advanced: true,
      blank: true,
      compare: true,
      equality: true,
      text: false,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: true,
      topN: true,
      tuple: false,
      identity: false,
      dynamic: false
    }
  },
  {
    name: 'DateTime',
    id: ValueDataType.DateTime,
    support: {
      advanced: true,
      blank: true,
      compare: true,
      equality: true,
      text: false,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: true,
      topN: true,
      tuple: false,
      identity: false,
      dynamic: false
    }
  },
  {
    name: 'Time',
    id: ValueDataType.TimeOnly,
    support: {
      advanced: true,
      blank: true,
      compare: true,
      equality: true,
      text: false,
      conditionJoin: true,

      basic: true,

      includeExclude: false,
      relativeDateOrTime: false,
      topN: true,
      tuple: false,
      identity: false,
      dynamic: false
    }
  },
];

export const DataTypeMap = <Record<ValueDataType, DataTypeConfig>>
  DataTypes.reduce((map, c) => (map[c.id] = c, map), <any>{});


export const FilterTypes = <FilterTypeConfig[]>[
  {
    name: 'Advanced',
    title: 'Advanced',
    id: FilterType.Advanced,
  },
  {
    name: 'Basic',
    title: 'Basic',
    id: FilterType.Basic
  },
  {
    name: 'RelativeDate',
    title: 'Relative Date',
    id: FilterType.RelativeDate
  },
  {
    name: 'RelativeTime',
    title: 'Relative Time',
    id: FilterType.RelativeTime
  },
  {
    name: 'TopN',
    title: 'TopN',
    id: FilterType.TopN
  },
  {
    name: 'Dynamic',
    title: 'Dynamic',
    id: FilterType.Dynamic,
  },
  {
    name: 'Expression',
    title: 'Expression',
    id: FilterType.Expr,
  },
];

export const FilterTypesMap = <Record<FilterType, FilterTypeConfig>>FilterTypes.reduce((map, c) => (map[c.id] = c, map), <any>{});


export interface DataTypeConfig {
  name: string;
  id: ValueDataType;

  support: {
    advanced: boolean;
    blank: boolean;
    compare: boolean;
    equality: boolean;
    text: boolean;
    conditionJoin: boolean;

    basic: boolean;

    includeExclude: boolean;
    relativeDateOrTime: boolean;
    topN: boolean;
    tuple: boolean;
    identity: boolean;

    dynamic: boolean;
  };

  operators: {
    advancedCondition: OperatorConfig<AdvancedFilterConditionOperator>[];
    advancedLogical: OperatorConfig<AdvancedFilterLogicalOperator>[];
    basic: OperatorConfig<BasicFilterOperator>[];
    relativeDateOrTime: OperatorConfig<RelativeDateOrTimeOperator>[];
    topN: OperatorConfig<TopNFilterOperator>[];
  };
}

export interface FilterTypeConfig {
  name: string;
  title: string;
  id: FilterType;
}


export enum OperatorKind {
  Blank = 'blank',
  Compare = 'compare',
  Equality = 'equality',
  Text = 'text',
  ConditionJoin = 'conditionJoin',
  Basic = 'basic',
  RelativeDateOrTime = 'relativeDateOrTime',
  TopN = 'topN',
  Tuple = 'tuple',
  Identity = 'identity'
}

export enum OperatorValueRequirement {
  NoValue = 'no-value',
  Required = 'required',
  AllowNull = 'allow-null',
}

export interface OperatorConfig<T> {
  name: string;
  title: string;
  kind: OperatorKind;
  id: T;
  valueRequirement?: OperatorValueRequirement;
  order?: number;
  orderForNumerics?: number;
}


export enum UnitKind {
  Date = 'date',
  Time = 'time'
}

export interface UnitConfig<T> {
  name: string;
  title: string;
  titleSingular: string;
  kind: UnitKind;
  id: T;
  fake?: boolean;
}

export const DateTimeUnitConfigs: UnitConfig<RelativeDateTimeFilterUnit>[] = [
  {
    name: 'Days',
    title: 'Days',
    titleSingular: 'Day',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.Days
  },
  {
    name: 'Weeks',
    title: 'Weeks',
    titleSingular: 'Week',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.Weeks
  },
  {
    name: 'CalendarWeeks',
    title: 'Calendar weeks',
    titleSingular: 'Calendar week',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.CalendarWeeks
  },
  {
    name: 'Months',
    title: 'Months',
    titleSingular: 'Month',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.Months
  },
  {
    name: 'CalendarMonths',
    title: 'Calendar months',
    titleSingular: 'Calendar month',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.CalendarMonths
  },
  {
    name: 'Quarters',
    title: 'Quarters',
    titleSingular: 'Quarter',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.Quarters
  },
  {
    name: 'CalendarQuarters',
    title: 'Calendar quarters',
    titleSingular: 'Calendar quarter',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.CalendarQuarters
  },
  {
    name: 'CalendarHalfYears',
    title: 'Calendar half years',
    titleSingular: 'Calendar half year',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.CalendarHalfYears
  },
  {
    name: 'Years',
    title: 'Years',
    titleSingular: 'Year',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.Years
  },
  {
    name: 'CalendarYears',
    title: 'Calendar years',
    titleSingular: 'Calendar year',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.CalendarYears
  },
  {
    name: 'SpendPeriods',
    title: 'Periods',
    titleSingular: 'Period',
    kind: UnitKind.Date,
    id: RelativeDateTimeFilterUnit.SpendPeriod
  },
  {
    name: 'Hours',
    title: 'Hours',
    titleSingular: 'Hour',
    kind: UnitKind.Time,
    id: RelativeDateTimeFilterUnit.Hours
  },
  {
    name: 'Minutes',
    title: 'Minutes',
    titleSingular: 'Minute',
    kind: UnitKind.Time,
    id: RelativeDateTimeFilterUnit.Minutes
  }
];

export const DateTimeUnitConfigsMap = DateTimeUnitConfigs.reduce((s, c) => (s[c.id] = c, s), <Record<RelativeDateTimeFilterUnit, UnitConfig<RelativeDateTimeFilterUnit>>>{});

// Pivot studio does not expose half years and periods as of now
export const PivotStudioDateTimeUnits =
  DateTimeUnitConfigs.filter(u => u.id != RelativeDateTimeFilterUnit.CalendarHalfYears && u.id != RelativeDateTimeFilterUnit.SpendPeriod);

export const DatesetEditorDateTimeUnits = <DateTimeUnitInfo[]>[
  { ...DateTimeUnitConfigsMap[RelativeDateTimeFilterUnit.CalendarMonths], title: 'Months', titleSingular: 'Month', exclusiveToProducts: [] },
  { ...DateTimeUnitConfigsMap[RelativeDateTimeFilterUnit.CalendarQuarters], title: 'Quarters', titleSingular: 'Quarter', exclusiveToProducts: [] },
  { ...DateTimeUnitConfigsMap[RelativeDateTimeFilterUnit.CalendarHalfYears], title: 'Half Years', titleSingular: 'Half Year', exclusiveToProducts: [] },
  { ...DateTimeUnitConfigsMap[RelativeDateTimeFilterUnit.CalendarYears], title: 'Years', titleSingular: 'Year', exclusiveToProducts: [] },
  { ...DateTimeUnitConfigsMap[RelativeDateTimeFilterUnit.SpendPeriod], title: 'Periods', titleSingular: 'Period', exclusiveToProducts: ['Spend']}
];

export const DatesetEditorDateTimeUnitsWithDays = [
  { ...DateTimeUnitConfigsMap[RelativeDateTimeFilterUnit.Days], title: 'Days', titleSingular: 'Day', exclusiveToProducts: [] },
  ...DatesetEditorDateTimeUnits
];

export interface DateTimeUnitInfo { title: string, titleSingular: string, exclusiveToProducts: string[], name: string, kind: UnitKind, id: RelativeDateTimeFilterUnit, fake?: boolean }

//#region Operator Mapping
export const AdvancedFilterConditionOperators = <OperatorConfig<AdvancedFilterConditionOperator>[]>[
  {
    name: 'IsBlank',
    title: 'Is blank',
    kind: OperatorKind.Blank,
    id: AdvancedFilterConditionOperator.IsBlank,
    valueRequirement: OperatorValueRequirement.NoValue,

    order: 900,
    orderForNumerics: 900,
  },
  {
    name: 'IsNotBlank',
    title: 'Is not blank',
    kind: OperatorKind.Blank,
    id: AdvancedFilterConditionOperator.IsNotBlank,
    valueRequirement: OperatorValueRequirement.NoValue,

    order: 901,
    orderForNumerics: 901,
  },

  {
    name: 'GreaterThan',
    title: 'Is greater than',
    kind: OperatorKind.Compare,
    id: AdvancedFilterConditionOperator.GreaterThan,
    valueRequirement: OperatorValueRequirement.Required,

    order: 102,
    orderForNumerics: 102,
  },
  {
    name: 'GreaterThanOrEqual',
    title: 'Is greater than or equal to',
    kind: OperatorKind.Compare,
    id: AdvancedFilterConditionOperator.GreaterThanOrEqual,
    valueRequirement: OperatorValueRequirement.Required,

    order: 103,
    orderForNumerics: 103,
  },

  {
    name: 'LessThan',
    title: 'Is less than',
    kind: OperatorKind.Compare,
    id: AdvancedFilterConditionOperator.LessThan,
    valueRequirement: OperatorValueRequirement.Required,

    order: 104,
    orderForNumerics: 104,
  },
  {
    name: 'LessThanOrEqual',
    title: 'Is less than or equal to',
    kind: OperatorKind.Compare,
    id: AdvancedFilterConditionOperator.LessThanOrEqual,
    valueRequirement: OperatorValueRequirement.Required,

    order: 105,
    orderForNumerics: 105,
  },

  {
    name: 'Is',
    title: 'Is',
    kind: OperatorKind.Equality,
    id: AdvancedFilterConditionOperator.Is,
    valueRequirement: OperatorValueRequirement.Required,

    order: 106,
    orderForNumerics: 106,
  },
  {
    name: 'IsNot',
    title: 'Is not',
    kind: OperatorKind.Equality,
    id: AdvancedFilterConditionOperator.IsNot,
    valueRequirement: OperatorValueRequirement.Required,

    order: 106,
    orderForNumerics: 106,
  },

  {
    name: 'Contains',
    title: 'Contains',
    kind: OperatorKind.Text,
    id: AdvancedFilterConditionOperator.Contains,
    valueRequirement: OperatorValueRequirement.Required,

    order: 107,
    orderForNumerics: 107,
  },
  {
    name: 'DoesNotContain',
    title: 'Does not contain',
    kind: OperatorKind.Text,
    id: AdvancedFilterConditionOperator.DoesNotContain,
    valueRequirement: OperatorValueRequirement.Required,

    order: 108,
    orderForNumerics: 108,
  },
  {
    name: 'StartsWith',
    title: 'Starts with',
    kind: OperatorKind.Text,
    id: AdvancedFilterConditionOperator.StartsWith,
    valueRequirement: OperatorValueRequirement.Required,

    order: 109,
    orderForNumerics: 109,
  },
  {
    name: 'DoesNotStartWith',
    title: 'Does not start with',
    kind: OperatorKind.Text,
    id: AdvancedFilterConditionOperator.DoesNotStartWith,
    valueRequirement: OperatorValueRequirement.Required,

    order: 110,
    orderForNumerics: 110,
  },
];

export const AdvancedFilterLogicalOperators = <OperatorConfig<AdvancedFilterLogicalOperator>[]>Object.keys(AdvancedFilterLogicalOperator).map(k => (
  {
    name: k,
    title: k,
    kind: OperatorKind.ConditionJoin,
    id: (AdvancedFilterLogicalOperator as any)[k]
  }
));

export const BasicFilterOperators = <OperatorConfig<BasicFilterOperator>[]>[
  {
    name: 'All',
    title: 'Any value',
    kind: OperatorKind.Basic,
    id: BasicFilterOperator.All,
  },
  {
    name: 'In',
    title: 'One of selected',
    kind: OperatorKind.Basic,
    id: BasicFilterOperator.In,
  },
  {
    name: 'NotIn',
    title: 'Not one of selected',
    kind: OperatorKind.Basic,
    id: BasicFilterOperator.NotIn,
  },
];

export const RelativeDateOrTimeFilterOperators = <OperatorConfig<RelativeDateOrTimeOperator>[]> [
  {
    name: 'InLast',
    title: 'In the past',
    kind: OperatorKind.RelativeDateOrTime,
    id: RelativeDateOrTimeOperator.InLast,
    valueRequirement: OperatorValueRequirement.Required,
  },
  {
    name: 'InNext',
    title: 'In the future',
    kind: OperatorKind.RelativeDateOrTime,
    id: RelativeDateOrTimeOperator.InNext,
    valueRequirement: OperatorValueRequirement.Required,
  },
  {
    name: 'InThis',
    title: 'In this',
    kind: OperatorKind.RelativeDateOrTime,
    id: RelativeDateOrTimeOperator.InThis,
    valueRequirement: OperatorValueRequirement.Required,
  },
  {
    name: 'OlderThan',
    title: 'Older than',
    kind: OperatorKind.RelativeDateOrTime,
    id: RelativeDateOrTimeOperator.OlderThan,
    valueRequirement: OperatorValueRequirement.Required,
  },
  {
    name: 'InRange',
    title: 'Range',
    kind: OperatorKind.RelativeDateOrTime,
    id: RelativeDateOrTimeOperator.InRange,
    valueRequirement: OperatorValueRequirement.Required,
  },
  {
    name: 'OutsideRange',
    title: 'Outside Range',
    kind: OperatorKind.RelativeDateOrTime,
    id: RelativeDateOrTimeOperator.OutsideRange,
    valueRequirement: OperatorValueRequirement.Required,
  },
];

export const TopNFilterOperators = <OperatorConfig<TopNFilterOperator>[]>Object.keys(TopNFilterOperator).map(k => (
  {
    name: k,
    title: k,
    kind: OperatorKind.TopN,
    id: (TopNFilterOperator as any)[k],
    valueRequirement: OperatorValueRequirement.Required,
  }
));

for (const dataTypeConfig of DataTypes) {
  dataTypeConfig.operators = {
    advancedCondition: GetSupportedItems(AdvancedFilterConditionOperators),
    advancedLogical: GetSupportedItems(AdvancedFilterLogicalOperators),
    basic: GetSupportedItems(BasicFilterOperators),
    relativeDateOrTime: GetSupportedItems(RelativeDateOrTimeFilterOperators),
    topN: GetSupportedItems(TopNFilterOperators),
  };

  const isNumeric = isNumericDataType(dataTypeConfig.id);

  if (isNumeric) {
    dataTypeConfig.operators.advancedCondition?.sort((a, b) => a.orderForNumerics - b.orderForNumerics);
  } else {
    dataTypeConfig.operators.advancedCondition?.sort((a, b) => a.order - b.order);
  }

  // Returns items where dataTypeConfig.support[item.kind] === true
  function GetSupportedItems(items: any[]) {
    const list: any[] = [];

    items.forEach(value => {
      if ((dataTypeConfig.support as any)[value.kind] === true)
        list.push(value);
    });

    return list;
  }
}
//#endregion


//#region Utilities
export const relativeDateTimeUnitToMomentUnit =
  new Map<RelativeDateTimeFilterUnit, moment.unitOfTime.Base | moment.unitOfTime._quarter>()
    .set(RelativeDateTimeFilterUnit.Days, 'days')
    .set(RelativeDateTimeFilterUnit.Weeks, 'weeks')
    .set(RelativeDateTimeFilterUnit.Months, 'months')
    .set(RelativeDateTimeFilterUnit.Quarters, 'quarters')
    .set(RelativeDateTimeFilterUnit.Years, 'years')

    .set(RelativeDateTimeFilterUnit.CalendarQuarters, 'quarters')
    .set(RelativeDateTimeFilterUnit.CalendarYears, 'years')
    .set(RelativeDateTimeFilterUnit.CalendarMonths, 'months')
    .set(RelativeDateTimeFilterUnit.CalendarWeeks, 'weeks')

    .set(RelativeDateTimeFilterUnit.SpendPeriod, 'months')

    .set(RelativeDateTimeFilterUnit.Minutes, 'minutes')
    .set(RelativeDateTimeFilterUnit.Hours, 'hours');

export function isCalendarUnit(unit: RelativeDateTimeFilterUnit) {
  return (unit == RelativeDateTimeFilterUnit.CalendarWeeks
    || unit == RelativeDateTimeFilterUnit.CalendarQuarters
    || unit == RelativeDateTimeFilterUnit.CalendarHalfYears
    || unit == RelativeDateTimeFilterUnit.CalendarMonths
    || unit == RelativeDateTimeFilterUnit.CalendarYears
    || unit == RelativeDateTimeFilterUnit.SpendPeriod)
}
//#endregion
